import { useState } from 'react';
import {
  ActionIcon,
  Anchor,
  Badge,
  Breadcrumbs,
  Center,
  Container,
  Group,
  Loader,
  Menu,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { IconChevronDown, IconCircleFilled, IconClock } from '@tabler/icons-react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useSubscription } from 'urql';
import { isUuid } from '@shared';
import { ErrorAlert } from '@/components/error-alert';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { useOrg } from '@/hooks/use-org';
import { isSoftphoneConnected } from '../-components/agent/agent-status';
import { elapsedSeconds, toMs } from '../-components/format';
import { useNow } from '../-components/use-now';
import { useSince } from '../-components/use-time-window';
import { QueueDashboardQueueSubscription, QueueDashboardStatusDefinitionsSubscription } from './-components/graphql';

const METRICS_WINDOW_MS = 60 * 60 * 1000;
const SYSTEM_MANAGED_STATUS_NAMES = ['ringing', 'on_call', 'wrap_up'];

export const Route = createFileRoute('/_authed/queue-dashboard/$queueId')({
  component: QueueDetailPage,
});

function MetricCard({
  label,
  value,
  suffix,
  color,
}: {
  label: string;
  value: number;
  suffix?: string;
  color?: string;
}) {
  return (
    <Paper p="md" radius="lg" withBorder>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text fw={700} size="xl" mt={4} c={color}>
        {value}
        {suffix && (
          <Text span size="sm" fw={400} c="dimmed">
            {suffix}
          </Text>
        )}
      </Text>
    </Paper>
  );
}

function QueueDetail({ organizationId, queueId }: { organizationId: string; queueId: string }) {
  const since = useSince(METRICS_WINDOW_MS);
  const now = useNow(5000);
  const [forceError, setForceError] = useState<string | null>(null);

  const { currentOrg, isPlatformOperatorView } = useOrg();
  const isSupervisor = isPlatformOperatorView || currentOrg?.role === 'owner' || currentOrg?.role === 'admin';

  const hasValidId = isUuid(queueId);

  const [{ data: queueData, error: queueError }] = useSubscription({
    query: QueueDashboardQueueSubscription,
    variables: { queueId, since },
    pause: !hasValidId,
  });
  const [{ data: definitionData, error: definitionError }] = useSubscription({
    query: QueueDashboardStatusDefinitionsSubscription,
    variables: { organizationId },
  });

  const queue = queueData?.queuesByPk ?? null;
  const loadError = queueError ?? definitionError;

  if (loadError) {
    return (
      <Container size="xl" py="xl">
        <ErrorAlert message={getErrorMessage(loadError, 'Unable to load this queue')} />
      </Container>
    );
  }

  if ((hasValidId && !queueData) || !definitionData) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!queue) {
    return (
      <Center h="50vh">
        <Text c="dimmed">Queue not found</Text>
      </Center>
    );
  }

  const forceStatus = async (memberId: string, statusName: string) => {
    setForceError(null);

    const { error } = await api.supervision['force-status'].post({ organizationId, memberId, statusName });

    if (error) setForceError(getErrorMessage(error, 'Unable to change the agent status'));
  };

  const handled = queue.recentEntries.filter((e) => e.status === 'answered' || e.status === 'completed');
  const abandoned = queue.recentEntries.filter((e) => e.status === 'abandoned');
  const slaTargetMs = (queue.slaTargetSecs ?? 60) * 1000;
  const waitMs = (entry: { enteredAt: string; answeredAt: string | null }) =>
    toMs(entry.answeredAt ?? entry.enteredAt) - toMs(entry.enteredAt);

  const metrics = {
    callsHandled: handled.length,
    avgWaitSecs:
      handled.length > 0 ? Math.round(handled.reduce((sum, e) => sum + waitMs(e), 0) / handled.length / 1000) : 0,
    slaPercent:
      handled.length > 0
        ? Math.round((handled.filter((e) => waitMs(e) <= slaTargetMs).length / handled.length) * 100)
        : 100,
    abandonRate: queue.recentEntries.length > 0 ? Math.round((abandoned.length / queue.recentEntries.length) * 100) : 0,
  };

  const activeCalls = queue.answered.aggregate?.count ?? 0;
  const definitionsByName = new Map(definitionData.agentStatusDefinitions.map((s) => [s.name, s]));

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Breadcrumbs mb="xs">
            <Anchor component={Link} to="/queue-dashboard" size="sm">
              Queue Dashboard
            </Anchor>
            <Text size="sm">{queue.name}</Text>
          </Breadcrumbs>
          <Group gap="md">
            <Title order={1}>{queue.name}</Title>
            <Badge color={queue.enabled ? 'green' : 'gray'} variant="light">
              {queue.enabled ? 'Active' : 'Disabled'}
            </Badge>
            <Badge variant="outline" tt="capitalize">
              {queue.routingStrategy.replace(/_/g, ' ')}
            </Badge>
          </Group>
        </div>

        <ErrorAlert message={forceError} />

        <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="md">
          <MetricCard label="Calls Handled" value={metrics.callsHandled} suffix=" /hr" />
          <MetricCard label="Avg Wait" value={metrics.avgWaitSecs} suffix="s" />
          <MetricCard
            label="SLA"
            value={metrics.slaPercent}
            suffix="%"
            color={metrics.slaPercent >= 80 ? 'green' : metrics.slaPercent >= 60 ? 'yellow' : 'red'}
          />
          <MetricCard
            label="Abandon Rate"
            value={metrics.abandonRate}
            suffix="%"
            color={metrics.abandonRate > 10 ? 'red' : undefined}
          />
          <MetricCard label="Active Calls" value={activeCalls} color={activeCalls > 0 ? 'green' : undefined} />
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          <Paper p="lg" radius="lg" withBorder>
            <Group justify="space-between" mb="md">
              <Text fw={600}>Waiting Callers</Text>
              <Badge color={queue.liveEntries.length > 0 ? 'orange' : 'gray'}>{queue.liveEntries.length}</Badge>
            </Group>
            {queue.liveEntries.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="md">
                No callers waiting
              </Text>
            ) : (
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Caller</Table.Th>
                    <Table.Th>Priority</Table.Th>
                    <Table.Th>Wait</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {queue.liveEntries.map((entry) => (
                    <Table.Tr key={entry.id}>
                      <Table.Td>
                        <Text size="sm" ff="monospace">
                          {entry.callerNumber}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="xs" color={entry.priority < 0 ? 'red' : 'gray'}>
                          {entry.priority}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          <IconClock size={12} />
                          <Text size="sm">{elapsedSeconds(entry.enteredAt, now)}s</Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="xs" color={entry.status === 'ringing' ? 'orange' : 'yellow'} variant="light">
                          {entry.status}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>

          <Paper p="lg" radius="lg" withBorder>
            <Group justify="space-between" mb="md">
              <Text fw={600}>Queue Agents</Text>
              <Badge color="indigo">{queue.queueMembers.length}</Badge>
            </Group>
            {queue.queueMembers.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="md">
                No agents assigned
              </Text>
            ) : (
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Agent</Table.Th>
                    <Table.Th>Ext</Table.Th>
                    <Table.Th>Status</Table.Th>
                    {isSupervisor && <Table.Th w={40} />}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {queue.queueMembers.map((queueMember) => {
                    const status = queueMember.member.agentStatuses[0] ?? null;
                    const statusName = isSoftphoneConnected(status, now)
                      ? (status?.statusName ?? 'offline')
                      : 'offline';

                    return (
                      <Table.Tr key={queueMember.id}>
                        <Table.Td>
                          <Text size="sm">{queueMember.member.email}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" ff="monospace" c="dimmed">
                            {queueMember.member.extensionNumber ?? '—'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <IconCircleFilled
                              size={8}
                              color={`var(--mantine-color-${definitionsByName.get(statusName)?.color ?? 'gray'}-6)`}
                            />
                            <Text size="sm" tt="capitalize">
                              {statusName.replace(/_/g, ' ')}
                            </Text>
                          </Group>
                        </Table.Td>
                        {isSupervisor && (
                          <Table.Td>
                            <Menu position="bottom-end">
                              <Menu.Target>
                                <ActionIcon variant="subtle" size="sm">
                                  <IconChevronDown size={14} />
                                </ActionIcon>
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Label>Force Status</Menu.Label>
                                {definitionData.agentStatusDefinitions
                                  .filter((definition) => !SYSTEM_MANAGED_STATUS_NAMES.includes(definition.name))
                                  .map((definition) => (
                                    <Menu.Item
                                      key={definition.name}
                                      leftSection={
                                        <IconCircleFilled
                                          size={8}
                                          color={`var(--mantine-color-${definition.color}-6)`}
                                        />
                                      }
                                      onClick={() => void forceStatus(queueMember.member.id, definition.name)}
                                    >
                                      {definition.label}
                                    </Menu.Item>
                                  ))}
                              </Menu.Dropdown>
                            </Menu>
                          </Table.Td>
                        )}
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            )}
          </Paper>
        </SimpleGrid>
      </Stack>
    </Container>
  );
}

function QueueDetailPage() {
  const { queueId } = Route.useParams();
  const { organizationId, isLoading } = useOrg();

  if (isLoading) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!organizationId) {
    return (
      <Center h="50vh">
        <Text c="dimmed">No organization selected</Text>
      </Center>
    );
  }

  return <QueueDetail organizationId={organizationId} queueId={queueId} />;
}
